import { useRef, useEffect } from 'react';
import type React from 'react';

import { Box } from 'ui/box';
import { Stack } from 'ui/stack';

import Badge from 'components/partials/badge/badge';
import { Loader } from 'ui/loader';
import { Typography } from 'ui/typography';
import Tooltip from 'components/partials/tooltip/tooltip';
import { useFeatureHintTarget } from 'components/shared/feature-hints';
import ChatMessageList from './chat-message-list';
import ChatComposer from './chat-composer';
import type { MessageInputHandle } from './message-input';
import NoMessagesContent from './no-messages-content';
import CohortsPopover from './cohorts-popover';
import MarkWalkthroughModal from '../walkthrough/mark-walkthrough-modal';

import { useMarkChat } from '../../hooks/use-mark-chat';
import { useInitialWalkthrough } from '../../hooks/use-initial-walkthrough';
import useExampleMessageSender from '../../hooks/use-example-message-sender';

interface AiChatProps {
  isSidebarOpen: boolean;
  conversationId: string | null;
  setSearchParams: (params: Record<string, string>) => void;
  clientId: string | number;
  registerFocusMessageInput?: (focus: () => void) => void;
  isReadOnly?: boolean;
}

const AiChat = ({
  isSidebarOpen,
  conversationId,
  setSearchParams,
  clientId,
  registerFocusMessageInput,
  isReadOnly,
}: AiChatProps): React.JSX.Element => {
  const messageInputRef = useRef<MessageInputHandle>(null);
  const hintTargetProps = useFeatureHintTarget('chat-input');

  useEffect(() => {
    registerFocusMessageInput?.(() => messageInputRef.current?.focus());
  }, [registerFocusMessageInput]);

  const {
    oktaUserId,
    messages,
    isLoadingConversation,
    isInputDisabled,
    isBlocked,
    limitReachedMessage,
    activeInputPart,
    sendMessage,
    retryMessage,
    regenerateMessage,
    editingMessage,
    startEditingMessage,
    cancelEditingMessage,
    submitEditedMessage,
    submitInputPart,
    dismissInputPart,
    messagesEndRef,
  } = useMarkChat({
    conversationId,
    clientId,
    isReadOnly,
    onConversationCreated: (id) => setSearchParams({ conversationId: id }),
    analyticsSurface: 'markPage',
  });

  const { isOpen: isInitialWalkthroughOpen, close: closeInitialWalkthrough } =
    useInitialWalkthrough(oktaUserId);

  useExampleMessageSender({
    conversationId,
    hasMessages: !!messages.length,
    isReadOnly,
    setSearchParams,
    sendMessage,
  });

  return (
    <Stack
      sx={[
        {
          position: 'relative',
          width: '100%',
          p: 2,
          minHeight: 0,
          maxHeight: 'calc(100vh - 8rem)',
          transitionProperty: 'margin-left',
          transitionDuration: '300ms',
          transitionTimingFunction: 'cubic-bezier(0, 0, 0.2, 1)',
        },
        isSidebarOpen && { ml: 32 },
        !isSidebarOpen && { ml: 0 },
      ]}
    >
      <Stack
        direction="row"
        sx={{ alignItems: 'center', justifyContent: 'space-between' }}
      >
        <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
          <Typography
            data-testid="ai-chat-heading"
            variant="h3"
            sx={{
              pl: 1,
              py: 1,
              flexShrink: 0,
              transitionProperty: 'margin-left',
              transitionDuration: '300ms',
              transitionTimingFunction: 'cubic-bezier(0, 0, 0.2, 1)',
              ml: isSidebarOpen ? 0 : '104px',
            }}
          >
            Ask MARK
          </Typography>
          <Tooltip
            position="bottom-start"
            content={
              <Box>
                <p className="font-semibold">MARK is in Public Preview</p>
                <p>
                  A late-stage development release for final testing and tuning.
                  MARK may make mistakes and does not yet include all data that
                  may be available across MARKETview. Please see the Guide & FAQ
                  document for details.
                </p>
              </Box>
            }
            customClass="!bg-gray-700 !text-white !px-2 !py-1 max-w-80"
          >
            <Badge text="Public Preview" variant="reserve-outlined" />
          </Tooltip>
        </Stack>
        <CohortsPopover clientId={clientId} />
      </Stack>

      {isLoadingConversation && !messages.length ? (
        <Loader loading fullScreen={false} backdrop="white" />
      ) : (
        <Stack
          sx={[
            { flex: '1 1 0%', minHeight: 0, gap: 2, width: '100%' },
            !!messages.length && { justifyContent: 'space-between' },
            !messages.length && { justifyContent: 'center' },
          ]}
        >
          {!isLoadingConversation && !messages.length && (
            <Box sx={{ maxWidth: '800px', mx: 'auto', width: '100%' }}>
              <NoMessagesContent
                onPropositionClick={sendMessage}
                isReadOnly={isReadOnly}
                isSendDisabled={isBlocked}
              />
            </Box>
          )}

          {!!messages.length && (
            <ChatMessageList
              messages={messages}
              oktaUserId={oktaUserId}
              clientId={clientId}
              conversationId={conversationId}
              isReadOnly={isReadOnly}
              isActionDisabled={isBlocked}
              isEditDisabled={isInputDisabled}
              onRetry={retryMessage}
              onRegenerate={regenerateMessage}
              onEditMessage={startEditingMessage}
              messagesEndRef={messagesEndRef}
              className="mt-9"
            />
          )}

          <ChatComposer
            isBlocked={isBlocked}
            limitReachedMessage={limitReachedMessage}
            activeInputPart={activeInputPart}
            onSubmitInputPart={submitInputPart}
            onDismissInputPart={dismissInputPart}
            onSendMessage={sendMessage}
            isInputDisabled={isInputDisabled}
            editingMessage={editingMessage}
            onSubmitEditedMessage={submitEditedMessage}
            onCancelEditingMessage={cancelEditingMessage}
            inputRef={messageInputRef}
            inputWrapperProps={hintTargetProps}
          />
        </Stack>
      )}

      {isInitialWalkthroughOpen && (
        <MarkWalkthroughModal
          onClose={closeInitialWalkthrough}
          entryPoint="firstVisit"
        />
      )}
    </Stack>
  );
};

export default AiChat;
